import { InteractiveContainer, Stroke } from '@acies/core';
import FileDropPickerEvent from './file-drop-picker-event';
import FileExtension from './file-extension';
import Label from './label';
import theme from './theme';

/**
 * The FileDropPicker UI component can be used when you want the user to select a file on the device and get a reference to that file.
 *
 * The user can either click and select the file or drag the file into the dashed drop zone.
 *
 * When a file is selected, a FileDropPickerEvent is dispatched with the file property that references the file selected.
 */
export default class FileDropPicker extends InteractiveContainer {
	public static override observedAttributes = [...InteractiveContainer.observedAttributes, 'description', 'extension', 'heading', 'mime-type'];

	/** The second text label will have the description property value as the text. */
	public set description(value: string) {
		if (this.#description === value) {
			return;
		}
		this.#description = value;
		this.#descriptionChanged = true;
		this.invalidateProperties();
	}

	public get description(): string {
		return this.#description;
	}

	/** The extension string will be displayed inside the blue box inside the file UI at the top. */
	public set extension(value: string) {
		if (this.#extension === value) {
			return;
		}
		this.#extension = value;
		this.#extensionChanged = true;
		this.invalidateProperties();
	}

	public get extension(): string {
		return this.#extension;
	}

	/** The first text label will have the heading property value as the text. */
	public set heading(value: string) {
		if (this.#heading === value) {
			return;
		}
		this.#heading = value;
		this.#headingChanged = true;
		this.invalidateProperties();
	}

	public get heading(): string {
		return this.#heading;
	}

	/** Set the mimeType property to a specific mime type when you want to narrow down the selection options to for example text/csv. */
	public set mimeType(value: string) {
		if (this.#mimeType === value) {
			return;
		}
		this.#mimeType = value;
		this.#mimeTypeChanged = true;
		this.invalidateProperties();
	}

	public get mimeType(): string {
		return this.#mimeType;
	}

	#description: string;
	#descriptionChanged: boolean;
	#descriptionLabel!: Label;
	#extension: string;
	#extensionChanged: boolean;
	#fileExtension!: FileExtension;
	#focusedStroke: Stroke;
	#heading: string;
	#headingChanged: boolean;
	#headingLabel!: Label;
	#idleStroke: Stroke;
	#input!: HTMLInputElement;
	#mimeType: string;
	#mimeTypeChanged: boolean;

	private get descriptionLabel(): Label {
		if (!this.#descriptionLabel) {
			this.#descriptionLabel = new Label();
		}
		return this.#descriptionLabel;
	}

	private get fileExtension(): FileExtension {
		if (!this.#fileExtension) {
			this.#fileExtension = new FileExtension();
			this.#fileExtension.extension = this.extension;
		}
		return this.#fileExtension;
	}

	private get headingLabel(): Label {
		if (!this.#headingLabel) {
			this.#headingLabel = new Label();
			this.#headingLabel.variant = 'dark';
		}
		return this.#headingLabel;
	}

	private get input(): HTMLInputElement {
		if (!this.#input) {
			this.#input = document.createElement('input');
			this.#input.style.appearance = 'none';
			this.#input.style.width = '100%';
			this.#input.style.height = '100%';
			this.#input.style.position = 'absolute';
			this.#input.style.opacity = '0';
			this.#input.style.cursor = 'pointer';
			this.#input.type = 'file';
			this.#input.accept = this.mimeType;
			this.#input.addEventListener('change', this.inputChangeHandler.bind(this));
			// the input is what the keyboard reaches and what opens the file dialog, so its focus is the one marked
			this.#input.addEventListener('focus', () => this.#setFocusedStyles());
			this.#input.addEventListener('blur', () => this.#setIdleStyles());
		}
		return this.#input;
	}

	public constructor() {
		super();
		this.alignment = 'center';
		this.autoLayout = 'vertical';
		this.cornerRadius = 16;
		this.fill = theme.colors.fill.weak;
		this.gapVertical = 16;
		this.paddingVertical = 24;
		this.paddingHorizontal = 24;
		this.#description = '';
		this.#descriptionChanged = false;
		this.#extension = 'EXT';
		this.#extensionChanged = false;
		this.#heading = '';
		this.#headingChanged = false;
		this.#mimeType = '*.*';
		this.#mimeTypeChanged = false;
		this.#focusedStroke = new Stroke(theme.colors.stroke.focus, 2, 'center', 'dashed');
		this.#idleStroke = new Stroke(theme.colors.stroke.soft, 2, 'center', 'dashed');
		this.stroke = this.#idleStroke;
	}

	protected override attributeChangedCallback(name: string, _: string, value: string) {
		super.attributeChangedCallback(name, _, value);
		if (name === 'description') {
			this.#descriptionAttributeChanged(value);
		} else if (name === 'extension') {
			this.#extensionAttributeChanged(value);
		} else if (name === 'heading') {
			this.#headingAttributeChanged(value);
		} else if (name === 'mime-type') {
			this.#mimeTypeAttributeChanged(value);
		}
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#descriptionChanged) {
			this.#commitDescription();
		}
		if (this.#extensionChanged) {
			this.#commitExtension();
		}
		if (this.#headingChanged) {
			this.#commitHeading();
		}
		if (this.#mimeTypeChanged) {
			this.#commitMimeType();
		}
	}

	/**
	 * Takes the picker itself out of the tab order, so the Tab key stops once, on the input that opens the file dialog.
	 *
	 * The picker would otherwise be a tab stop of its own in front of the input, and one where Space and Enter do nothing. It is done here rather than in the constructor because it sets tabIndex, and an element made by document.createElement must not have an attribute when its constructor returns.
	 */
	protected override initialize(): void {
		this.tabbable = false;
		super.initialize();
		this.#createChildren();
	}

	#commitDescription(): void {
		this.#descriptionChanged = false;
		this.descriptionLabel.text = this.description;
	}

	#commitExtension(): void {
		this.#extensionChanged = false;
		this.fileExtension.extension = this.extension;
	}

	#commitHeading(): void {
		this.#headingChanged = false;
		this.headingLabel.text = this.heading;
	}

	#commitMimeType(): void {
		this.#mimeTypeChanged = false;
		this.input.accept = this.mimeType;
	}

	#createChildren(): void {
		this.addComponents([this.fileExtension, this.headingLabel, this.descriptionLabel]);
		this.appendChild(this.input);
	}

	#descriptionAttributeChanged(value: string): void {
		this.description = value;
	}

	#extensionAttributeChanged(value: string): void {
		this.extension = value;
	}

	#headingAttributeChanged(value: string): void {
		this.heading = value;
	}

	#mimeTypeAttributeChanged(value: string): void {
		this.mimeType = value;
	}

	#setFocusedStyles(): void {
		this.stroke = this.#focusedStroke;
	}

	#setIdleStyles(): void {
		this.stroke = this.#idleStroke;
	}

	private inputChangeHandler(): void {
		if (this.input && this.input.files) {
			if (this.input.files[0]) {
				this.dispatchEvent(new FileDropPickerEvent(this.input.files[0]));
				// reset the value so we can pick the same file again
				this.input.value = '';
			}
		}
	}
}
customElements.define('ac-file-drop-picker', FileDropPicker);
